// useQueryParser.ts — debounced NL → compiled query parser hook (PRI-291)
// Spec §17: debounces prompt changes, calls /v1/parse, handles smart paste

import { useState, useEffect } from 'react'
import { parsePrompt, isCompiledQueryJSON } from '../lib/api/parse'
import type { CompiledQuery } from '../components/v2/compiled-query-types'
import { trackQueryParsed, trackQueryParseFailed } from '../telemetry/events'

export function useQueryParser(
  prompt: string,
  debounceMs = 400,
): {
  query: CompiledQuery | null
  parsing: boolean
  error: string | null
  parseMode: 'haiku' | 'heuristic' | null
} {
  const [query, setQuery] = useState<CompiledQuery | null>(null)
  const [parsing, setParsing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [parseMode, setParseMode] = useState<'haiku' | 'heuristic' | null>(null)

  useEffect(() => {
    // Empty prompt: reset immediately, no API call
    if (!prompt.trim()) {
      setQuery(null)
      setParsing(false)
      setError(null)
      setParseMode(null)
      return
    }

    // Smart paste: detect raw JSON and bypass API
    const pastedQuery = isCompiledQueryJSON(prompt)
    if (pastedQuery) {
      setQuery(pastedQuery)
      setParsing(false)
      setError(null)
      setParseMode(null)
      return
    }

    const controller = new AbortController()

    const timer = setTimeout(async () => {
      setParsing(true)
      setError(null)
      const startedAt = (typeof performance !== 'undefined' ? performance.now() : Date.now())
      try {
        const result = await parsePrompt(prompt, controller.signal)
        setQuery(result.compiled_query)
        setParseMode(result.parse_mode)
        setParsing(false)
        try {
          trackQueryParsed({
            parse_mode: result.parse_mode,
            prompt_length: prompt.length,
            duration_ms: Math.round(
              (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt,
            ),
          })
        } catch { /* analytics best-effort */ }
      } catch (err: unknown) {
        if ((err as { name?: string }).name === 'AbortError') return
        const e = err as { status?: number; message?: string }
        const isParseFailed = e.status === 422 || e.message === 'parse_failed'
        if (isParseFailed) {
          setError("Darwin doesn't understand this prompt — try something more specific")
        } else {
          setError('Network error — check your connection and try again')
        }
        setQuery(null)
        setParsing(false)
        try {
          trackQueryParseFailed({
            error_type: isParseFailed ? 'parse_failed' : 'network_error',
            prompt_length: prompt.length,
            status: e.status,
          })
        } catch { /* analytics best-effort */ }
      }
    }, debounceMs)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [prompt, debounceMs])

  return { query, parsing, error, parseMode }
}
